<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <div v-else class="doc-view-container">
    <!-- Header -->
    <div class="doc-header">
      <div class="flex items-center gap-3">
        <h1 class="doc-title">{{ t('documentation') }}</h1>
        <span v-if="documentation.externalID" class="doc-number">
          #{{ documentation.externalID }}
        </span>
      </div>
      <router-link :to="editUrl" class="edit-btn">
        <Icon icon="mdi:pencil-outline" class="text-lg" />
        {{ t('edit') }}
      </router-link>
    </div>

    <!-- Card -->
    <div class="doc-card">
      <!-- Contact Information Section -->
      <div class="doc-section">
        <div class="section-header">
          <span class="section-dot"></span>
          <span class="section-title">{{ t('contactInformation') }}</span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
          <div class="doc-field">
            <div class="field-icon">
              <Icon icon="mdi:file-document-outline" />
            </div>
            <div>
              <div class="field-label">{{ t('name') }}</div>
              <div class="field-value">{{ documentation.name || '—' }}</div>
            </div>
          </div>

          <div class="doc-field">
            <div class="field-icon">
              <Icon icon="mdi:phone-outline" />
            </div>
            <div>
              <div class="field-label">{{ t('phone') }}</div>
              <div class="field-value" style="direction: ltr">{{ documentation.phone || '—' }}</div>
            </div>
          </div>

          <div class="doc-field">
            <div class="field-icon">
              <Icon icon="mdi:message-outline" />
            </div>
            <div>
              <div class="field-label">{{ t('contactVia') }}</div>
              <div class="field-value">{{ contactViaLabel || '—' }}</div>
            </div>
          </div>

          <div class="doc-field">
            <div class="field-icon">
              <Icon icon="mdi:email-outline" />
            </div>
            <div>
              <div class="field-label">{{ t('subject') }}</div>
              <div class="field-value">{{ documentation.subject || '—' }}</div>
            </div>
          </div>
        </div>
      </div>

      <hr class="section-divider" />

      <!-- Details Section -->
      <div class="doc-section">
        <div class="section-header">
          <span class="section-dot"></span>
          <span class="section-title">{{ t('details') }}</span>
        </div>

        <div class="mt-5 space-y-6">
          <div class="doc-field">
            <div class="field-icon">
              <Icon icon="mdi:note-text-outline" />
            </div>
            <div>
              <div class="field-label">{{ t('note') }}</div>
              <div class="field-value">{{ documentation.note || '—' }}</div>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="doc-field">
              <div class="field-icon">
                <Icon icon="mdi:account-outline" />
              </div>
              <div>
                <div class="field-label">{{ t('addedBy') }}</div>
                <div class="field-value">{{ documentation.user?.name || '—' }}</div>
              </div>
            </div>
          </div>

          <!-- Related Task -->
          <div class="mt-6 doc-section">
            <div class="section-header mb-3">
              <span class="section-dot"></span>
              <span class="section-title">{{ t('relatedTask') }}</span>
            </div>
            <InlineTaskEditor
              :task="documentation.task"
              :users="users"
              :note-id="documentation.id || ''"
              hide-header
              @task-created="onTaskCreated"
              @task-deleted="onTaskDeleted"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, onBeforeMount, ref} from 'vue'

import {useHTTP} from '@/plugins/http'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import InlineTaskEditor from './InlineTaskEditor.vue'
import {useRoute} from 'vue-router'
import {useI18n} from 'vue-i18n'
import Documentation, {documentationI18nMessages} from '@/models/customer/Documentation'
import Task from '@/models/hr/Task'
import User from '@/models/user/User'
import {formatPostgresDate} from '@/utils/date'
import {Route as $Route} from '@/router/routes'
import {Icon} from '@iconify/vue'

const extendedMessages = {
  en: {
    ...documentationI18nMessages.en,
    contactInformation: 'Contact Information',
    details: 'Details',
    edit: 'Edit',
    addedBy: 'Added By',
  },
  ar: {
    ...documentationI18nMessages.ar,
    contactInformation: 'معلومات الاتصال',
    details: 'التفاصيل',
    edit: 'تعديل',
    addedBy: 'أضيف بواسطة',
  }
}

export default defineComponent({
  name: 'note-view',
  components: {
    LoadingSpinner,
    AlertBadge,
    InlineTaskEditor,
    Icon
  },
  setup() {
    const documentation = ref(new Documentation())
    const error = ref({title: '', body: ''})
    const http = useHTTP()
    const loading = ref(true)
    const route = useRoute()

    const {t} = useI18n({
      useScope: 'global',
      messages: extendedMessages
    })

    onBeforeMount(async () => {
      const url = `${Documentation.ENDPOINT}/${route.params.id}`

      try {
        const resp = await http.get<Documentation>(url)
        documentation.value = Documentation.from(resp)
      } catch (errorResp) {
        error.value.title = errorResp.title
        error.value.body = errorResp.body
      }

      loading.value = false
    })

    const editUrl = computed(() => {
      return $Route.CUSTOMERS_NOTES_EDIT.replace(':id', route.params.id as string)
    })

    const contactViaLabel = computed(() => {
      const map: Record<string, string> = {
        phone_call: t('phoneCall'),
        message: t('message'),
        in_person: t('inPerson'),
      }
      return map[documentation.value.contactVia] || documentation.value.contactVia
    })

    const users = ref<User[]>([])
    http.get<User[]>('/user/users').then(res => { users.value = res || [] }).catch(() => {})

    const onTaskCreated = (task: Task) => { documentation.value.task = task }
    const onTaskDeleted = () => { documentation.value.task = null }

    return {
      documentation,
      error,
      loading,
      editUrl,
      contactViaLabel,
      users,
      onTaskCreated,
      onTaskDeleted,
      t,
      formatPostgresDate
    }
  }
})
</script>

<style scoped>
.doc-view-container {
  @apply p-6 md:p-8;
}

.doc-header {
  @apply flex items-center justify-between mb-6;
}

.doc-title {
  @apply text-2xl font-bold text-gray-900;
}

.doc-number {
  @apply text-base text-gray-400 font-normal;
}

.edit-btn {
  @apply flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 rounded-lg text-sm font-medium no-underline transition-colors;
}

.edit-btn:hover {
  @apply bg-gray-700;
}

.doc-card {
  @apply bg-white rounded-xl border border-gray-200 p-6 md:p-8;
}

.doc-section {
  @apply py-2;
}

.section-header {
  @apply flex items-center gap-2;
}

.section-dot {
  @apply w-2 h-2 rounded-full bg-blue-600;
}

.section-title {
  @apply text-sm font-semibold text-blue-600 uppercase tracking-wide;
}

.section-divider {
  @apply border-0 border-t border-gray-200 my-6;
}

.doc-field {
  @apply flex items-start gap-3;
}

.field-icon {
  @apply w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-lg flex-shrink-0 mt-0.5;
}

.field-label {
  @apply text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1;
}

.field-value {
  @apply text-base font-medium text-gray-900;
}
</style>
